import { useEffect, useState } from 'react'
import { FileIcon, FileText } from 'lucide-react'
import { cn } from '@/lib/cn'
import { artworkExt, artworkKind, renderPdfFirstPage } from '@/lib/artwork'

/** A PDF page heavy enough to stall pdf.js gets the file card instead of "Loading" forever. */
const PDF_RENDER_TIMEOUT_MS = 20_000

/**
 * Inline preview of a customer artwork file, built for a job sheet that gets
 * printed and handed to production staff: everything it shows is a plain
 * <img>, because an embedded PDF viewer (iframe/embed) renders on screen but
 * comes out blank under window.print().
 *
 * - png / jpg render directly (svg too, on orders old enough to carry one;
 *   the upload no longer accepts it).
 * - pdf is rasterized client-side with pdf.js (first page) into a data URL.
 *   Modern .ai files are PDF-compatible, so those that carry a pdf mime get a
 *   preview for free.
 * - ai / eps, and any file whose preview fails (a 58,000-operation vector
 *   page on MPG-2026-00103), show a file card instead: an empty numbered slot
 *   on the sheet read as a missing file.
 */
export default function ArtworkPreview({
  url,
  name,
  mime,
  className,
  imageClassName = 'max-h-72 print:max-h-[7cm]',
}: {
  url: string
  name?: string | null
  mime?: string | null
  className?: string
  /** The image's height caps; replaces the default rather than merging, since cn() does not dedupe. */
  imageClassName?: string
}) {
  const kind = artworkKind(mime, name, url)
  const [broken, setBroken] = useState(false)
  const [pdfImage, setPdfImage] = useState<string | null>(null)

  useEffect(() => {
    if (kind !== 'pdf') return
    let cancelled = false
    const timer = window.setTimeout(() => {
      console.error('artwork PDF preview timed out', url)
      if (!cancelled) setBroken(true)
    }, PDF_RENDER_TIMEOUT_MS)

    renderPdfFirstPage(url)
      .then((preview) => {
        if (!cancelled) setPdfImage(preview.dataUrl)
      })
      .catch((error) => {
        console.error('artwork PDF preview failed', error)
        if (!cancelled) setBroken(true)
      })
      .finally(() => window.clearTimeout(timer))

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [kind, url])

  if (kind === 'other' || broken) {
    return <FileCard url={url} name={name} mime={mime} className={className} note="No preview - open the file or scan its QR" />
  }

  if (kind === 'pdf' && !pdfImage) {
    // The card holds the slot while pdf.js works, and if printing beats the
    // render the paper still shows which file sits there.
    return <FileCard url={url} name={name} mime={mime} className={className} note="Loading preview..." />
  }

  return (
    // className replaces the default margin rather than joining it: cn() does
    // not dedupe, and a leftover mt-3 beside a full-height figure pushed the
    // job sheet mockup 12px down over the file name and its QR.
    <figure className={className ?? 'mt-3'}>
      <img
        src={kind === 'pdf' ? pdfImage! : url}
        alt={name ?? 'Customer artwork'}
        className={cn('mx-auto w-auto max-w-full rounded border border-line object-contain', imageClassName)}
        onError={() => setBroken(true)}
      />
      {kind === 'pdf' && <figcaption className="mt-1 text-center text-2xs text-muted">PDF - page 1</figcaption>}
    </figure>
  )
}

/** The file's type and name in the picture's place; a link on screen, a labelled box on paper. */
function FileCard({
  url,
  name,
  mime,
  className,
  note,
}: {
  url: string
  name?: string | null
  mime?: string | null
  className?: string
  note: string
}) {
  const ext = artworkExt({ name, path: url, mime })
  const Icon = ext === 'PDF' ? FileText : FileIcon

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={cn(
        'flex min-h-40 flex-col items-center justify-center gap-1 rounded border-2 border-dashed border-line px-3 py-4 text-center hover:border-navy print:min-h-0',
        className ?? 'mt-3',
      )}
    >
      <Icon className="h-10 w-10 shrink-0 text-navy" strokeWidth={1.5} />
      <span className="text-lg font-extrabold leading-none text-navy">{ext}</span>
      {name && <span className="w-full truncate text-xs font-semibold">{name}</span>}
      <span className="text-2xs text-muted">{note}</span>
    </a>
  )
}
